<script setup>
import { showModal } from '@/Utils/modalBus'
import { APP_EVENTS, onAppEvent, emitAppEvent } from '@/Utils/appEventBus'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'

import _ from 'lodash'
import { mediaPulseModel } from '@/components/Composable/mediaPulse'
import StreamService from '@/services/StreamService'
import PlayoutService from '@/services/PlayoutService'

import PlayoutHeaderV2 from '@/components/TVPlayoutV2/PlayoutHeaderV2.vue'
import PlayoutPreviewSection from '@/components/TVPlayoutV2/PlayoutPreviewSection.vue'
import PlayoutScheduleTab from '@/components/TVPlayoutV2/PlayoutScheduleTab.vue'
import PlayoutFilesTab from '@/components/TVPlayoutV2/PlayoutFilesTab.vue'
import PlayoutAddSourceModal from '@/components/TVPlayoutV2/PlayoutAddSourceModal.vue'
import PlayoutLimitDetailModal from '@/components/TVPlayoutV2/PlayoutLimitDetailModal.vue'
import PlayoutUploadModal from '@/components/ui/PlayoutUploadModal.vue'
import AlertModal from '@/components/modals/AlertModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Badge from '@/components/Badge.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import Spinner from '@/components/ui/Spinner.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { notify } = useNotification()

let unsubscribePlayoutFileEvents = []

const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

const scopeAlive = ref(true)
const loading = ref(true)
const configLoaded = ref(false)
const configSyncing = ref(false)
const fileStatsLoaded = ref(false)
const activeTab = ref('schedule')

const playoutUploadDirs = ref([])
const loadingPlayoutDirs = ref(false)
const showUploadModal = ref(false)
const showAddSourceModal = ref(false)
const showLimitDetailModal = ref(false)
const oversizedFilesCount = ref(0)

const playout = computed(() => store.getters['Streams/tvPlayout'])
const playoutConfig = computed(() => store.getters['Playout/playoutConfig'])
const playlist = computed(() => store.getters['Playout/playlist'] || [])
const playoutDuration = computed(() => store.getters['Playout/playoutDuration'])
const playoutMaxDurationLimit = computed(() => store.getters['User/playoutMaxDurationLimit'])
const tvPlayoutLineUpDuration = computed(() => store.getters['User/tvPlayoutLineUpDuration'])

const maxUploadFileGb = computed(() => store.getters['User/tvPlayoutMaxUploadFileGB'] || 0)
const maxUploadFileBytes = computed(() => maxUploadFileGb.value * (1000 ** 3))
const maxFilesNum = computed(() => store.getters['User/tvPlayoutMaxUploadFilesNum'] || 0)
const maxFileDurationMin = computed(() => store.getters['User/tvPlayoutMaxUploadFileDurationMin'] || 0)
const maxFileDurationSec = computed(() => maxFileDurationMin.value * 60)

const overDurationFilesCount = ref(0)
const uploadedFilesCount = ref(0)

// Aggregated plan-limit status used by the schedule warning banner and the
// "TV Playout Limit Details" modal. File count, oversized and over-duration
// files are derived by scanning each upload directory.
const playoutLimits = computed(() => {
	const filesNumExceeded = maxFilesNum.value > 0 && uploadedFilesCount.value >= maxFilesNum.value
	const fileSizeExceeded = oversizedFilesCount.value > 0
	const fileDurationExceeded = overDurationFilesCount.value > 0
	return {
		filesNumExceeded,
		fileSizeExceeded,
		fileDurationExceeded,
		anyExceeded: filesNumExceeded || fileSizeExceeded || fileDurationExceeded,
		// File count alone does not block scheduling — the user can still set a
		// schedule with the files they already have (further uploads stay blocked).
		// Only oversized / over-duration files block "Set Schedule".
		blocksSchedule: fileSizeExceeded || fileDurationExceeded,
		uploadedFilesCount: uploadedFilesCount.value,
		maxFilesNum: maxFilesNum.value,
		oversizedCount: oversizedFilesCount.value,
		overDurationCount: overDurationFilesCount.value,
		maxFileGb: maxUploadFileGb.value,
		maxFileDurationMin: maxFileDurationMin.value,
	}
})

// The schedule tab does not mount the file directory rows, so the per-file
// limit counters are computed here (independently of the active tab) by listing
// the files in each upload directory and comparing against the plan limits.
const refreshPlayoutFileStats = async () => {
	const id = playout.value?._id
	if (!id) {
		uploadedFilesCount.value = 0
		oversizedFilesCount.value = 0
		overDurationFilesCount.value = 0
		return
	}
	const maxBytes = maxUploadFileBytes.value
	const maxDurationSec = maxFileDurationSec.value
	const dirs = playoutUploadDirs.value || []
	const results = await Promise.all(
		dirs.map(async (dir) => {
			const dirId = dir.id || dir._id
			if (!dirId) return { total: 0, oversized: 0, overDuration: 0 }
			try {
				const files = await StreamService.listPlayoutDirFiles(id, dirId)
				if (!Array.isArray(files)) return { total: 0, oversized: 0, overDuration: 0 }
				return {
					total: files.length,
					oversized: maxBytes
						? files.filter((file) => file.bytes && file.bytes > maxBytes).length
						: 0,
					overDuration: maxDurationSec
						? files.filter(
								(file) => file.mediaInfo?.duration && file.mediaInfo.duration > maxDurationSec,
							).length
						: 0,
				}
			} catch {
				return { total: 0, oversized: 0, overDuration: 0 }
			}
		}),
	)
	uploadedFilesCount.value = results.reduce((sum, r) => sum + r.total, 0)
	oversizedFilesCount.value = results.reduce((sum, r) => sum + r.oversized, 0)
	overDurationFilesCount.value = results.reduce((sum, r) => sum + r.overDuration, 0)
}

const vodStreams = computed(() => store.getters['Streams/vodStreams'] || [])
const liveToVodFoldersCount = computed(
	() => vodStreams.value.filter((stream) => stream.liveParent).length,
)

const scheduleCount = computed(() => playlist.value?.length || 0)
const filesCount = computed(
	() => (playoutUploadDirs.value?.length || 0) + liveToVodFoldersCount.value,
)

const refreshPlayoutDirectories = async () => {
	const id = playout.value?._id
	if (!id) {
		playoutUploadDirs.value = []
		return
	}
	loadingPlayoutDirs.value = true
	try {
		const data = await StreamService.listPlayoutDirs(id)
		playoutUploadDirs.value = Array.isArray(data) ? data : []
	} catch {
		playoutUploadDirs.value = []
	} finally {
		loadingPlayoutDirs.value = false
	}
}

// After a file is uploaded or deleted the server-side directory stats change,
// so refresh the directory list, the config and the per-file limit counters.
const onPlayoutFilesChanged = async () => {
	await refreshPlayoutDirectories()
	if (playout.value?._id) {
		await store.dispatch('Playout/getPlayoutConfig', playout.value._id)
	}
	await refreshPlayoutFileStats()
}

const redirectToPlayoutList = async () => {
	notify({ group: 'error', text: 'Failed to load TV Playout data' })
	await router.push({ name: 'TVPlayoutDashboardV2' })
}

const snapshotStoreConfig = () => JSON.stringify(store.getters['Playout/playoutConfig'] ?? null)

const loadPlayoutConfig = async () => {
	const tvId = route.params?.tvId
	const cached = store.getters['Playout/cachedConfig'](tvId)
	if (!cached) {
		configLoaded.value = false
		try {
			await store.dispatch('Playout/getPlayoutConfig', tvId)
			configLoaded.value = true
		} catch (err) {
			console.log(err)
			await redirectToPlayoutList()
		}
		return
	}

	await store.dispatch('Playout/seedConfig', cached)
	configLoaded.value = true
	const seededSnapshot = snapshotStoreConfig()
	configSyncing.value = true
	try {
		const fresh = await PlayoutService.getPlayoutConfig(tvId)
		if (!scopeAlive.value || route.params?.tvId !== tvId) return
		if (_.isEqual(fresh, cached)) {
			store.dispatch('Playout/cacheConfig', { id: tvId, data: fresh })
		} else if (snapshotStoreConfig() === seededSnapshot) {
			await store.dispatch('Playout/applyFreshConfig', { id: tvId, config: fresh })
		} else {
			store.dispatch('Playout/cacheConfig', { id: tvId, data: fresh })
		}
	} catch (err) {
		console.log(err)
		await redirectToPlayoutList()
	} finally {
		if (route.params?.tvId === tvId) configSyncing.value = false
	}
}

const loadDirsAndFileStats = async () => {
	fileStatsLoaded.value = false
	try {
		await refreshPlayoutDirectories()
		await refreshPlayoutFileStats()
	} finally {
		fileStatsLoaded.value = true
	}
}

const loadPlayoutDataInBackground = () => {
	loadPlayoutConfig()
	loadDirsAndFileStats()
}

const bindPlayoutSession = () => {
	subscribeMediaPulse(playout.value)
	loadPlayoutDataInBackground()
	if (unsubscribePlayoutFileEvents.length) return
	unsubscribePlayoutFileEvents = [
		onAppEvent(APP_EVENTS.VIDEO_UPLOADED, onPlayoutFilesChanged),
		onAppEvent(APP_EVENTS.PLAYOUT_FILE_REMOVED, onPlayoutFilesChanged),
		// "View playlist" from the Files tab (in-use delete guard) jumps here.
		onAppEvent(APP_EVENTS.PLAYOUT_VIEW_SCHEDULE, () => switchTab('schedule')),
	]
}

const resolvePlayout = async () => {
	if (playout.value?._id) {
		loading.value = false
		void store.dispatch('Streams/getStreams', true).catch((err) => {
			if (scopeAlive.value) console.error('Failed to refresh TV Playouts', err)
		})
		return true
	}

	loading.value = true
	try {
		await store.dispatch('Streams/getStreams', true)
		if (!playout.value?._id) {
			await redirectToPlayoutList()
			return false
		}
		return true
	} catch (err) {
		console.log(err)
		await redirectToPlayoutList()
		return false
	} finally {
		if (scopeAlive.value) loading.value = false
	}
}

onMounted(async () => {
	if (!(await resolvePlayout())) return
	bindPlayoutSession()
})

onUnmounted(() => {
	unsubscribeMediaPulse()
	scopeAlive.value = false
	store.dispatch('Playout/clear')
	unsubscribePlayoutFileEvents.forEach((unsubscribe) => unsubscribe())
	unsubscribePlayoutFileEvents = []
})

watch(
	() => route.params.tvId,
	async (id, previousId) => {
		if (!scopeAlive.value || previousId === undefined || String(id) === String(previousId)) return
		configLoaded.value = false
		fileStatsLoaded.value = false
		if (!(await resolvePlayout())) return
		unsubscribeMediaPulse()
		store.dispatch('Playout/clear')
		bindPlayoutSession()
	},
)

const setSchedule = () => {
	if (playout.value?.enabled && !mediaPulse.value) {
		subscribeMediaPulse(playout.value)
	}
}

const addToPlayout = (stream) => {
	const durationLimit = playoutMaxDurationLimit.value * 1000 * 60 * 60
	const streamDuration = stream ? stream.mediaInfo?.duration * 1000 : 600000
	if (playoutConfig.value?.mode === 'schedule') {
		store.dispatch('Playout/addItem', stream)
	} else if (playoutDuration.value + streamDuration >= durationLimit) {
		showModal('modal-overload-duration')
	} else {
		store.dispatch('Playout/addItem', stream)
	}
}

const onAddFileFromFilesTab = (file) => {
	const normalized = {
		...file,
		_id: file._id || file.id,
		id: file.id || file._id,
		// Files uploaded to a playout directory use the 'playout-upload' source
		// type; live-to-vod recordings remain 'vod'.
		type: file._playoutUpload ? 'playout-upload' : 'vod',
	}
	addToPlayout(normalized)
}

const onAddStreamFromFilesTab = (stream) => {
	const normalized = { ...stream, type: 'live' }
	addToPlayout(normalized)
}

const createFolder = async (name) => {
	try {
		await StreamService.createPlayoutDir(playout.value._id, name)
		await refreshPlayoutDirectories()
		notify({ group: 'success', text: `Directory "${name}" created` })
	} catch (err) {
		notify({ group: 'error', text: err?.message || 'Failed to create directory' })
	}
}

const handleUploadFile = ({ file, dirId }) => {
	// Playout uploads go through the shared background Uppy widget.
	// Files are stored under the directory id, referenced via _playoutDir.
	// Pass the directory's display name so the widget shows the right target.
	const dir = (playoutUploadDirs.value || []).find((d) => (d.id || d._id) === dirId)
	const streamForUpload = { ...playout.value, _playoutDir: dirId, _playoutDirName: dir && dir.name }
	emitAppEvent(APP_EVENTS.OPEN_VOD_UPLOADER, { stream: streamForUpload, files: [file] })
}

const switchTab = (tab) => {
	activeTab.value = tab
}

const onAddSource = () => {
	showAddSourceModal.value = true
}

const onBrowseFiles = () => {
	switchTab('files')
}

const onViewLimitDetail = () => {
	showLimitDetailModal.value = true
}

const onGoToFiles = () => {
	switchTab('files')
}

const onUpgradePlan = () => {
	router.push({ name: 'Payments', query: { category: 'live' } })
}
</script>

<template>
	<div class="w-full min-w-0 container detail-shell px-4 mt-4 lg:pt-8 lg:self-start">
		<Spinner
			v-if="loading"
			text="Retrieving data..."
			classes="text-surface-8 mt-96"
			spinner-color="var(--c-dark-8)"
			spinner-size="15px"
		/>
		<template v-else>
			<PlayoutHeaderV2 :media-pulse="mediaPulse" @enable-stream="setSchedule" />

			<div class="mt-6">
				<PlayoutPreviewSection :media-pulse="mediaPulse" />
			</div>

			<section class="mt-6 min-w-0 max-w-full">
				<TabGroup type="lines" class="mb-4">
					<TabItem
						type="line"
						:isActive="activeTab === 'schedule'"
						:onClick="() => switchTab('schedule')"
					>
						Schedule
						<Badge type="secondary" inline pill size="xs" class="ml-2">{{ scheduleCount }}</Badge>
					</TabItem>
					<TabItem
						type="line"
						:isActive="activeTab === 'files'"
						:onClick="() => switchTab('files')"
					>
						Files
						<Badge v-if="!loadingPlayoutDirs" type="secondary" inline pill size="xs" class="ml-2">{{ filesCount }}</Badge>
					</TabItem>
				</TabGroup>

				<PlayoutScheduleTab
					v-if="activeTab === 'schedule'"
					:media-pulse="mediaPulse"
					:config-loaded="configLoaded"
					:limits-loading="!fileStatsLoaded || configSyncing"
					:schedule-blocked="playoutLimits.blocksSchedule"
					@enable-stream="setSchedule"
					@browse-files="onBrowseFiles"
					@add-source="onAddSource"
					@view-limit-detail="onViewLimitDetail"
				/>
				<PlayoutFilesTab
					v-else
					:playout-id="playout?._id"
					:upload-dirs="playoutUploadDirs"
					:loading-upload-dirs="loadingPlayoutDirs"
					@open-upload-modal="showUploadModal = true"
					@create-folder="createFolder"
					@add-file="onAddFileFromFilesTab"
					@add-stream="onAddStreamFromFilesTab"
				/>
			</section>
		</template>

		<PlayoutUploadModal
			:visible="showUploadModal"
			:directories="playoutUploadDirs"
			@close="showUploadModal = false"
			@upload="handleUploadFile"
		/>
		<PlayoutAddSourceModal
			v-model:visible="showAddSourceModal"
			:playout-id="playout?._id"
			:upload-dirs="playoutUploadDirs"
			@add-file="onAddFileFromFilesTab"
			@add-stream="onAddStreamFromFilesTab"
		/>
		<PlayoutLimitDetailModal
			v-model:visible="showLimitDetailModal"
			:limits="playoutLimits"
			@go-to-files="onGoToFiles"
			@upgrade="onUpgradePlan"
		/>
		<AlertModal
			modalId="modal-overload-duration"
			message="Unable to add Video to Timetable"
			buttonAlignment="text-right"
			okText="Ok, got it"
			body-class="text-pretty"
		>
			This video exceeds the duration limit of {{ tvPlayoutLineUpDuration }} hours. To
			increase the limit, contact
			<FormButton type="link" onclick="Intercom('show')" customClasses="inline underline text-white">
				sales team
			</FormButton>.
		</AlertModal>
	</div>
</template>

<style scoped></style>
